import { useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Flex,
  Stack,
  HStack,
  Box,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  Spacer,
  Text
} from '@chakra-ui/react'
import ReactSelect from 'react-select'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { useAllClients } from '../../hooks/clients'
import { useMutateVisit } from '../../hooks/visits'
import SimpleSpinner from '../../components/Shared/loaders/SimpleSpinner'
import CheckInput from '../../components/Shared/inputs/CheckInput'
import SimpleInput from '../../components/Shared/inputs/SimpleInput'
import {
  TODAY_ISOSTRING,
  isStrictClientAreaSelectionEnabled
} from '../../components/Shared/const'

const validationSchema = Yup.object().shape({
  id_client: Yup.string().required('Ingrese cliente'),
  id_branch: Yup.string().nullable(),
  area: Yup.string(),
  requiredCreatedAt: Yup.boolean(),
  customCreatedAt: undefined
})

const getClientAreas = (clients, clientId) => {
  if (!clientId || !clients) return []

  const client = clients.find(
    (item) => item.id.toString() === clientId.toString()
  )

  return client?.locations || []
}

// Formulario nueva visita
const NewVisit = () => {
  const history = useHistory()
  const { user } = useSelector((state) => state.auth)
  const { data: clients, isLoading: loadingGet } = useAllClients()
  const { mutate, isLoading: loadingPost, reset } = useMutateVisit('CREATE')
  const [sortBy, setSortBy] = useState('alpha')

  // Lista de clientes ordenada (alfabético o fecha de creación)
  const clientOptions = useMemo(() => {
    const list = [...(clients || [])]
    if (sortBy === 'recent') {
      list.sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )
    } else {
      list.sort((a, b) =>
        (a.company_name || a.name || '').localeCompare(
          b.company_name || b.name || '',
          'es',
          { sensitivity: 'base' }
        )
      )
    }
    return list.map((client) => ({
      value: String(client.id),
      label: client.company_name || client.name
    }))
  }, [clients, sortBy])

  const formik = useFormik({
    validationSchema,
    initialValues: {
      id_client: '',
      id_branch: null,
      area: '',
      created_by: user.id,
      requiredCreatedAt: false,
      customCreatedAt: new Date()
    },
    onSubmit: (values) => {
      const clientAreas = getClientAreas(clients, values.id_client)
      const strictAreaSelection = isStrictClientAreaSelectionEnabled()

      if (strictAreaSelection) {
        if (clientAreas.length === 0) {
          formik.setFieldError(
            'area',
            'Configure las áreas del cliente en el mantenedor'
          )
          return
        }

        if (!values.area) {
          formik.setFieldError('area', 'Seleccione un área')
          return
        }
      } else if (!values.area?.trim()) {
        formik.setFieldError('area', 'Ingrese un área')
        return
      }

      if (!values.id_branch) {
        delete values.id_branch
      }
      mutate(values, {
        onSuccess: () => {
          reset()
          formik.resetForm()
          history.push(`/visitas`)
        },
        onError: (err) => {
          reset()
          console.error(err)
        }
      })
    }
  })

  const clientAreas = getClientAreas(clients, formik.values.id_client)
  const hasConfiguredAreas = clientAreas.length > 0
  const strictAreaSelection = isStrictClientAreaSelectionEnabled()
  const canSubmitVisit =
    formik.values.id_client &&
    (strictAreaSelection
      ? hasConfiguredAreas && Boolean(formik.values.area)
      : Boolean(formik.values.area?.trim()))

  const handleBack = () => {
    history.push(`/visitas`)
  }

  const handleClientSelect = (option) => {
    formik.setFieldValue('id_client', option ? option.value : '')
    formik.setFieldValue('area', '')
    formik.setFieldValue('id_branch', null)
  }

  const selectedClientOption =
    clientOptions.find(
      (option) => option.value === String(formik.values.id_client)
    ) || null

  // Obtener lista de sucursales
  const branchs = (client) => {
    const branchsList = clients.find((data) => data.id.toString() === client)
    return branchsList ? branchsList.branchs : []
  }

  useEffect(() => {
    if (!formik.values.requiredCreatedAt) {
      formik.setFieldValue('customCreatedAt', undefined)
    }
  }, [formik.values.requiredCreatedAt])

  return (
    <Stack spacing={8} py={12} px={6}>
      <Stack align={'left'}>
        <Heading fontSize={'4xl'} color={'#36355F'}>
          Nueva Visita
        </Heading>
      </Stack>
      <Stack spacing={10} align={'center'}>
        {!loadingGet ? (
          <FormControl
            id="id_client"
            isInvalid={
              formik.touched.id_client && Boolean(formik.errors.id_client)
            }
          >
            <Flex
              justify="space-between"
              align={{ base: 'start', md: 'center' }}
              direction={{ base: 'column', md: 'row' }}
              gap={2}
              mb={1}
            >
              <FormLabel mb={0}>Cliente</FormLabel>
              <HStack spacing={2}>
                <Text fontSize="13px" color="#718096" whiteSpace="nowrap">
                  Ordenar por
                </Text>
                <Select
                  size="sm"
                  w="auto"
                  borderRadius="md"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                >
                  <option value="alpha">Orden alfabético</option>
                  <option value="recent">Fecha de creación</option>
                </Select>
              </HStack>
            </Flex>
            <Box>
              <ReactSelect
                inputId="id_client"
                options={clientOptions}
                value={selectedClientOption}
                onChange={handleClientSelect}
                placeholder="Busque y seleccione un cliente"
                noOptionsMessage={() => 'Sin resultados'}
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#E2E8F0',
                    minHeight: '40px'
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? '#59BABA'
                      : state.isFocused
                      ? '#D1F1F5'
                      : '#FFFFFF',
                    color: state.isSelected ? '#FFFFFF' : '#36355F'
                  })
                }}
              />
            </Box>
            <FormErrorMessage>
              {formik.touched.id_client && formik.errors.id_client}
            </FormErrorMessage>
          </FormControl>
        ) : (
          <SimpleSpinner />
        )}
        {formik.values.id_client ? (
          <>
            <FormControl
              id="id_branch"
              isInvalid={
                formik.touched.id_branch && Boolean(formik.errors.id_branch)
              }
            >
              <FormLabel>Sucursal</FormLabel>
              <Select
                placeholder="Seleccione"
                name="id_branch"
                value={formik.values.id_branch || ''}
                onChange={formik.handleChange}
              >
                {branchs(formik.values.id_client).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                {formik.touched.id_branch && formik.errors.id_branch}
              </FormErrorMessage>
            </FormControl>
            {strictAreaSelection ? (
              hasConfiguredAreas ? (
                <FormControl
                  id="area"
                  isInvalid={formik.touched.area && Boolean(formik.errors.area)}
                >
                  <FormLabel>Área del cliente</FormLabel>
                  <Select
                    placeholder="Seleccione"
                    name="area"
                    value={formik.values.area}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {clientAreas.map((location) => (
                      <option key={location.id} value={location.name}>
                        {location.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {formik.touched.area && formik.errors.area}
                  </FormErrorMessage>
                </FormControl>
              ) : (
                <Text color="orange.600" fontSize="sm">
                  Este cliente no tiene áreas configuradas. Configure las áreas a
                  tratar en el mantenedor de clientes antes de iniciar la visita.
                </Text>
              )
            ) : (
              <SimpleInput
                label="Área del cliente"
                name="area"
                placeholder="Ingrese el área manualmente"
                value={formik.values.area}
                errors={formik.touched.area && Boolean(formik.errors.area)}
                errorMsg={formik.touched.area && formik.errors.area}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}
            <CheckInput
              name="requiredCreatedAt"
              label="¿Desea Establecer una fecha de creación específica?"
              value={formik.values.requiredCreatedAt}
              onChange={formik.handleChange}
            />
            {formik.values.requiredCreatedAt && (
              <SimpleInput
                label="Fecha de Creación"
                name="customCreatedAt"
                type="date"
                max={TODAY_ISOSTRING}
                value={formik.values.customCreatedAt}
                errors={Boolean(formik.errors.customCreatedAt)}
                errorMsg={formik.errors.customCreatedAt}
                onChange={formik.handleChange}
              />
            )}
          </>
        ) : null}
      </Stack>
      <Flex spacing={10} align="right" pt="15px">
        <Spacer />
        <Button
          mr="5px"
          align="left"
          onClick={handleBack}
          bg="#F2F1FB"
          color="#59BABA"
          _hover={{
            bg: 'grey.800'
          }}
        >
          Cancelar
        </Button>
        <Button
          ml="5px"
          align="right"
          isLoading={loadingPost}
          onClick={formik.handleSubmit}
          bg="#59BABA"
          color={'white'}
          _hover={{
            bg: 'teal.600'
          }}
          disabled={loadingPost || !canSubmitVisit}
        >
          Siguiente
        </Button>
      </Flex>
    </Stack>
  )
}

export default NewVisit
